import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Button,
  Card,
  Progress,
  Chip,
  CircularProgress,
  BottomSheet,
} from '../../components';
import { useInspectionContext, useApiKey } from '../../hooks';
import { getAngleLabel, fetchPhotoBlobUrl } from '../../api';
import { formatRelativeTime, getStatusLabel, getStatusChipVariant, getGradeLabel, getGradeColor } from '../../utils';
import type { InspectionReport, Damage } from '../../types/api';

export const StatusPage: React.FC = () => {
  const navigate = useNavigate();
  const { inspectionId } = useParams<{ inspectionId?: string }>();
  const { apiKey } = useApiKey();
  const {
    state,
    startPolling,
    stopPolling,
    fetchReport,
    reset,
  } = useInspectionContext();

  const [report, setReport] = useState<InspectionReport | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [showErrorDetail, setShowErrorDetail] = useState(false);
  const [lastPolled, setLastPolled] = useState<Date | null>(null);

  // Get inspection ID from URL or state
  const currentInspectionId = inspectionId || state.inspectionId;

  useEffect(() => {
    if (currentInspectionId) {
      startPolling(currentInspectionId);
    }
    return () => stopPolling();
  }, [currentInspectionId, startPolling, stopPolling]);

  // Update last polled time when status changes
  useEffect(() => {
    if (state.serverStatus) {
      setLastPolled(new Date());
    }
  }, [state.serverStatus, state.serverUpdatedAt]);

  // Fetch report when done
  useEffect(() => {
    if (state.serverStatus === 'done' && currentInspectionId && !report) {
      fetchReport(currentInspectionId).then((r) => {
        if (r) {
          setReport(r);
          setShowReport(true);
        }
      });
    }
  }, [state.serverStatus, currentInspectionId, report, fetchReport]);

  const handleNewInspection = () => {
    reset();
    navigate('/inspecao/placa', { replace: true });
  };

  const handleViewHistory = () => {
    navigate('/inspecao/historico', { replace: true });
  };

  const handleRetry = () => {
    if (currentInspectionId) {
      // Would need reprocess API call
      navigate(`/inspecao/captura`, { replace: true });
    }
  };

  if (!currentInspectionId) {
    return (
      <Page
        appBar={{ title: 'Status', elevation: false, transparent: true }}
      >
        <div className="flex flex-col items-center justify-center flex-1 text-center px-4">
          <span className="material-symbols-outlined text-on-surface-variant text-6xl mb-4">search_off</span>
          <h2 className="font-headline-lg">Nenhuma vistoria em andamento</h2>
          <p className="font-body-md text-on-surface-variant mt-2">Inicie uma nova vistoria para acompanhar o status.</p>
          <Button variant="primary" onClick={handleNewInspection} className="mt-6" leftIcon={<span className="material-symbols-outlined">add</span>}>
            Nova Vistoria
          </Button>
        </div>
      </Page>
    );
  }

  const status = state.serverStatus || 'processing';
  const isProcessing = status === 'processing';
  const isDone = status === 'done';
  const isFailed = status === 'failed';

  return (
    <Page
      appBar={{
        title: 'Status da Vistoria',
        subtitle: state.plate,
        elevation: false,
        transparent: true,
      }}
      className="pb-24"
    >
      <div className="flex flex-col gap-6">
        {/* Status Card */}
        <Card variant="elevated" padding="xl" className="text-center">
          <div className="mb-6">
            {isProcessing && (
              <CircularProgress
                value={50}
                size={80}
                strokeWidth={6}
                className="text-primary mx-auto"
              >
                <span className="material-symbols-outlined text-primary text-2xl">hourglass_top</span>
              </CircularProgress>
            )}
            {isDone && (
              <div className="w-24 h-24 rounded-full bg-success-bg flex items-center justify-center mx-auto">
                <span className="material-symbols-outlined text-success text-4xl">check_circle</span>
              </div>
            )}
            {isFailed && (
              <div className="w-24 h-24 rounded-full bg-error-container flex items-center justify-center mx-auto">
                <span className="material-symbols-outlined text-error text-4xl">error</span>
              </div>
            )}
          </div>

          <h2 className="font-headline-lg mb-2">{getStatusLabel(status)}</h2>
          
          <Chip
            variant={getStatusChipVariant(status)}
            className="mx-auto mb-4"
            icon={
              isProcessing ? <span className="material-symbols-outlined animate-spin-slow">sync</span> :
              isDone ? <span className="material-symbols-outlined">check</span> :
              <span className="material-symbols-outlined">error</span>
            }
          >
            {getStatusLabel(status)}
          </Chip>

          {state.serverUpdatedAt && (
            <p className="font-body-sm text-on-surface-variant">
              Atualizado {formatRelativeTime(state.serverUpdatedAt)}
            </p>
          )}

          {lastPolled && (
            <p className="font-body-sm text-on-surface-variant mt-1">
              Última verificação: {formatRelativeTime(lastPolled)}
            </p>
          )}

          {isFailed && state.failureReason && (
            <div className="mt-4 p-3 rounded-lg bg-error-container">
              <p className="font-label-sm text-on-error-container mb-1">Motivo da falha:</p>
              <p className="font-body-sm text-on-error-container font-mono text-xs">{state.failureReason}</p>
              <Button variant="outline" size="sm" onClick={() => setShowErrorDetail(true)} className="mt-2">
                Ver detalhes
              </Button>
            </div>
          )}
        </Card>

        {/* Progress for processing */}
        {isProcessing && (
          <Card variant="elevated" padding="lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-label-lg">Progresso do Pipeline</h3>
              <Chip variant="processing" size="sm">Em andamento</Chip>
            </div>
            <Progress value={50} max={100} size="md" showLabel label="Processamento de IA" />
            <p className="font-body-sm text-on-surface-variant mt-3 text-center">
              A análise geralmente leva 30-60 segundos. Você pode fechar o app e voltar depois.
            </p>
          </Card>
        )}

        {/* Report Preview (when done) */}
        {isDone && report && (
          <Card variant="elevated" padding="lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-label-lg">Resultado da Análise</h3>
              <Chip
                variant={getGradeColor(report.grade) as any}
                icon={<span className="material-symbols-outlined">grade</span>}
              >
                Nota {report.grade} — {getGradeLabel(report.grade)}
              </Chip>
            </div>

            <div className="flex items-center justify-center gap-8 mb-4">
              <div className="text-center">
                <p className="font-display-lg text-primary tabular-nums">{report.score}</p>
                <p className="font-label-sm text-on-surface-variant">Score</p>
              </div>
              <div className="w-px h-12 bg-outline-variant" />
              <div className="text-center">
                <p className="font-display-lg text-on-surface tabular-nums">{report.damages.length}</p>
                <p className="font-label-sm text-on-surface-variant">Avarias</p>
              </div>
              <div className="w-px h-12 bg-outline-variant" />
              <div className="text-center">
                <p className="font-display-lg text-on-surface tabular-nums">{report.optional_items.filter(o => o.detected).length}</p>
                <p className="font-label-sm text-on-surface-variant">Opcionais</p>
              </div>
            </div>

            <Button
              variant="primary"
              fullWidth
              onClick={() => setShowReport(true)}
              leftIcon={<span className="material-symbols-outlined">description</span>}
            >
              Ver Laudo Completo
            </Button>
          </Card>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {isProcessing && (
            <>
              <Button variant="outline" fullWidth onClick={handleNewInspection}>
                Nova Vistoria
              </Button>
              <Button variant="ghost" fullWidth onClick={handleViewHistory}>
                Ver Histórico
              </Button>
            </>
          )}

          {isDone && (
            <>
              <Button variant="primary" fullWidth onClick={() => setShowReport(true)} leftIcon={<span className="material-symbols-outlined">description</span>}>
                Ver Laudo Completo
              </Button>
              <Button variant="outline" fullWidth onClick={handleNewInspection} leftIcon={<span className="material-symbols-outlined">add</span>}>
                Nova Vistoria
              </Button>
              <Button variant="ghost" fullWidth onClick={handleViewHistory}>
                Ver Histórico
              </Button>
            </>
          )}

          {isFailed && (
            <>
              <Button variant="primary" fullWidth onClick={handleRetry} leftIcon={<span className="material-symbols-outlined">refresh</span>}>
                Tentar Novamente
              </Button>
              <Button variant="outline" fullWidth onClick={handleNewInspection} leftIcon={<span className="material-symbols-outlined">add</span>}>
                Nova Vistoria
              </Button>
              <Button variant="ghost" fullWidth onClick={handleViewHistory}>
                Ver Histórico
              </Button>
            </>
          )}
        </div>

        {/* Debug Info (dev) */}
        {import.meta.env.DEV && currentInspectionId && (
          <Card variant="outlined" padding="sm">
            <p className="font-label-sm text-on-surface-variant">
              ID: <code className="font-body-sm tabular-nums">{currentInspectionId}</code>
            </p>
          </Card>
        )}
      </div>

      {/* Report Bottom Sheet */}
      <BottomSheet
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        title="Laudo da Vistoria"
        subtitle={`${state.plate} • ${report?.completed_at ? formatRelativeTime(report.completed_at) : ''}`}
      >
        {report && (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Grade & Score */}
            <Card variant="elevated" padding="lg" className="text-center">
              <Chip
                variant={getGradeColor(report.grade) as any}
                size="sm"
                className="mb-3"
              >
                Nota {report.grade} — {getGradeLabel(report.grade)}
              </Chip>
              <p className="font-display-lg text-primary tabular-nums">{report.score}</p>
              <p className="font-label-sm text-on-surface-variant mt-1">Score de 0 a 100</p>
            </Card>

            {/* Damages */}
            {report.damages.length > 0 && (
              <div>
                <h3 className="font-label-lg mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-warning">warning</span>
                  Avarias Detectadas ({report.damages.length})
                </h3>
                <div className="space-y-3">
                  {report.damages.map((damage) => (
                    <Card key={damage.id} variant="interactive" padding="md">
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-surface-container flex items-center justify-center flex-shrink-0">
                          <DamagePhoto damage={damage} apiKey={apiKey ?? undefined} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-label-md">{damage.label}</h4>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Chip variant={damage.severity === 'grave' ? 'error' : damage.severity === 'moderado' ? 'warning' : 'info'} size="sm">
                              {damage.severity}
                            </Chip>
                            {damage.angle && (
                              <Chip variant="default" size="sm">
                                {getAngleLabel(damage.angle)}
                              </Chip>
                            )}
                            {damage.confidence !== undefined && (
                              <Chip variant="default" size="sm">
                                Confiança: {Math.round(damage.confidence * 100)}%
                              </Chip>
                            )}
                          </div>
                          <p className="font-body-sm text-on-surface-variant mt-2">
                            Foto com destaque da IA — o retângulo indica a região da avaria.
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Optional Items */}
            {report.optional_items.length > 0 && (
              <div>
                <h3 className="font-label-lg mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-secondary">tune</span>
                  Itens Opcionais
                </h3>
                <div className="space-y-2">
                  {report.optional_items.map((item, idx) => (
                    <Card key={idx} variant="default" padding="md">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            item.detected ? 'bg-success-bg' : 'bg-surface-container-high'
                          }`}>
                            <span className={`material-symbols-outlined ${item.detected ? 'text-success' : 'text-on-surface-variant'}`}>
                              {item.detected ? getOptionalItemIcon(item.name) : 'close'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-label-md">{item.name}</h4>
                            <p className="font-body-sm text-on-surface-variant">
                              {item.detected ? 'Detectado' : 'Não detectado'} • {Math.round(item.confidence * 100)}% confiança
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <Card variant="outlined" padding="md">
              <p className="font-label-sm text-on-surface-variant">Concluído em {report.completed_at ? formatRelativeTime(report.completed_at) : 'N/A'}</p>
              <p className="font-label-sm text-on-surface-variant mt-1">Versão do laudo: {report.report_version}</p>
            </Card>
          </div>
        )}
      </BottomSheet>

      {/* Error Detail Bottom Sheet */}
      <BottomSheet
        isOpen={showErrorDetail}
        onClose={() => setShowErrorDetail(false)}
        title="Detalhes do Erro"
      >
        <div className="space-y-3">
          <Card variant="outlined" padding="md">
            <p className="font-label-sm text-error">Código: {state.failureReason}</p>
            <p className="font-body-sm text-on-surface-variant mt-2">
              {getErrorDescription(state.failureReason || '')}
            </p>
          </Card>
          <Button variant="primary" fullWidth onClick={handleRetry} leftIcon={<span className="material-symbols-outlined">refresh</span>}>
            Tentar Novamente
          </Button>
          <Button variant="outline" fullWidth onClick={handleNewInspection}>
            Nova Vistoria
          </Button>
        </div>
      </BottomSheet>
    </Page>
  );
};

/**
 * Foto do damage — carrega a versão ANOTADA pelo worker (retângulo colorido
 * por severidade) com fallback para a original. O endpoint exige X-Api-Key,
 * então <img src> direto não funciona: fetch → blob → object URL.
 */
function DamagePhoto({ damage, apiKey }: { damage: Damage; apiKey?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [variant, setVariant] = useState<'annotated' | 'original'>('annotated');

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      const path =
        variant === 'annotated'
          ? damage.annotated_photo_url || damage.original_photo_url
          : damage.original_photo_url;
      try {
        const url = await fetchPhotoBlobUrl(path, apiKey);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setSrc(url);
      } catch {
        if (cancelled) return;
        // Anotada ainda não gerada (worker não detectou nada no ângulo) → original
        if (variant === 'annotated' && damage.original_photo_url) {
          setVariant('original');
        } else {
          setFailed(true);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [variant, damage.annotated_photo_url, damage.original_photo_url, apiKey]);

  if (failed || !src) {
    return <span className="material-symbols-outlined text-on-surface-variant">crop_free</span>;
  }
  return <img src={src} alt={damage.label} className="w-full h-full object-cover" />;
}

/**
 * Ícone por categoria de diagnóstico (catálogo v1.3 §1.3) — fumaça,
 * vazamentos, ferrugem, riscos e confirmação de vídeo analisado.
 */
function getOptionalItemIcon(name: string): string {
  if (name.startsWith('Fumaça azul')) return 'air';
  if (name.startsWith('Fumaça branca persistente')) return 'water_drop';
  if (name.startsWith('Fumaça branca passageira')) return 'cloud';
  if (name.startsWith('Fumaça preta')) return 'local_fire_department';
  if (name.startsWith('Gotejamento')) return 'water_drop';
  if (name.startsWith('Vazamento de óleo')) return 'opacity';
  if (name.startsWith('Vazamento de coolant')) return 'invert_colors';
  if (name.startsWith('Ferrugem')) return 'texture';
  if (name.startsWith('Riscos')) return 'gesture';
  if (name.startsWith('Vídeo')) return 'videocam';
  return 'check';
}

function getErrorDescription(reason: string): string {
  const descriptions: Record<string, string> = {
    'image_quality:front_too_dark': 'A foto frontal está muito escura. Recapture com melhor iluminação.',
    'image_quality:rear_too_dark': 'A foto traseira está muito escura. Recapture com melhor iluminação.',
    'image_quality:side_right_too_dark': 'A foto lateral direita está muito escura.',
    'image_quality:side_left_too_dark': 'A foto lateral esquerda está muito escura.',
    'image_quality:dashboard_too_dark': 'A foto do painel está muito escura. Ligue as luzes internas.',
    'image_quality:engine_too_dark': 'A foto do motor está muito escura. Use lanterna se necessário.',
    'image_quality:trunk_too_dark': 'A foto do porta-malas está muito escura.',
    'image_quality:chassis_too_dark': 'A foto do chassi está muito escura.',
    'ai_model_error': 'Ocorreu um erro interno no modelo de IA. Tentaremos reprocessar automaticamente.',
    'timeout': 'O processamento demorou mais que o esperado. A vistoria será reprocessada.',
    'expired': 'Esta vistoria expirou. Por favor, inicie uma nova.',
    'not_found': 'Vistoria não encontrada no servidor.',
  };
  return descriptions[reason] || 'Ocorreu um erro desconhecido. Entre em contato com o suporte.';
}